import { inject, Observer } from "mobx-react"
import { WithRouterProps } from "next/dist/client/with-router"
import Head from "next/head"
import * as React from "react"
import styled, { css } from "styled-components"

import Button from "~/components/atoms/Button"
import FunctionLink from "~/components/atoms/FunctionLink"
import Icon from "~/components/atoms/Icon"
import Pc from "~/components/atoms/Pc"
import SnsButton from "~/components/atoms/SnsButton"
import Sp from "~/components/atoms/Sp"
import Typography from "~/components/atoms/Typography"
import { Container as LayoutContainer } from "~/components/layouts/Container"
import Dialog from "~/components/molecules/Dialog"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import { Color, IsMobile, LineHeight, RadiusSize, Shadow, SpacingSize, TextSize, Z } from "~/consts/style-mixins"
import { internalLink, staticPath } from "~/services/helper"
import UiStore from "~/stores/UiStore"
import UserStore from "~/stores/UserStore"

interface StyledProps {
  uiStore: UiStore
  userStore: UserStore
}

interface State {
  isLoading: boolean
}

// Page

@inject("uiStore", "userStore")
class NewGradJobConsultation extends React.Component<WithRouterProps & StyledProps, State> {
  state = {
    isLoading: false,
  }
  render() {
    const { uiStore, userStore } = this.props
    const { isLoading } = this.state
    const toggleLoading = () => {
      this.setState({
        isLoading: !this.state.isLoading,
      })
    }
    const applyConsultation = async () => {
      toggleLoading()
      // 従来はここで API を叩いていたが、エンドポイント側を既に削除済み
      uiStore.toggleApplyConsultationModal()
      toggleLoading()
    }
    return (
      <>
        <Head>
          <title>就活対策や相談、厳選企業をご紹介！キャリア面談 | Infra（インフラ）</title>
          <meta
            name="description"
            content="就活対策やキャリア相談、厳選企業のご紹介を行う、キャリア面談です。キャリアアドバイザーと一緒に就活の方向性を定め、改善を繰り返すことで、行きたい企業に内定しましょう！"
          />
          <meta property="og:title" content="就活対策や相談、厳選企業をご紹介！キャリア面談 | Infra（インフラ）" />
          <meta
            property="og:description"
            content="就活対策やキャリア相談、厳選企業のご紹介を行う、キャリア面談です。キャリアアドバイザーと一緒に就活の方向性を定め、改善を繰り返すことで、行きたい企業に内定しましょう！"
          />
          <meta
            property="og:image"
            content={`${process.env.APP_HOST + staticPath("/static/images/new-grad-job/consultation/ogp.png")}`}
          />
        </Head>
        <Header logoType="Consultation" showTab />
        <Observer>
          {() => (
            <Container>
              <Pc>
                <img
                  src={`${staticPath("/static/images/new-grad-job/consultation/decoration-1.svg")}`}
                  className="decoration d1"
                />
              </Pc>
              <Sp>
                <img
                  src={`${staticPath("/static/images/new-grad-job/consultation/decoration-1_sp.svg")}`}
                  className="decoration d1"
                />
              </Sp>
              <Section>
                <MainVisual
                  onClick={() => {
                    userStore.authed ? applyConsultation() : uiStore.toggleRegistrationModal()
                  }}
                  isLoading={isLoading}
                  graduatesIn={userStore.user ? userStore.user.graduatesIn : undefined}
                />
                <img
                  src={`${staticPath("/static/images/new-grad-job/consultation/decoration-2.svg")}`}
                  className="decoration d2"
                />
                <img
                  src={`${staticPath("/static/images/new-grad-job/consultation/decoration-3.svg")}`}
                  className="decoration d3"
                />
              </Section>
              <Section>
                <img
                  src={`${staticPath("/static/images/new-grad-job/consultation/decoration-4.svg")}`}
                  className="decoration d4"
                />
                <About />
                <img
                  src={`${staticPath("/static/images/new-grad-job/consultation/decoration-5.svg")}`}
                  className="decoration d5"
                />
              </Section>
              <Section>
                <img
                  src={`${staticPath("/static/images/new-grad-job/consultation/decoration-6.svg")}`}
                  className="decoration d6"
                />
                <Recommend />
              </Section>
              <Section>
                <img
                  src={`${staticPath("/static/images/new-grad-job/consultation/decoration-7.svg")}`}
                  className="decoration d7"
                />
                <CvAppeal
                  onClick={() => {
                    userStore.authed ? applyConsultation() : uiStore.toggleRegistrationModal()
                  }}
                  isLoading={isLoading}
                  graduatesIn={userStore.user ? userStore.user.graduatesIn : undefined}
                />
              </Section>
              <Section>
                <Flow />
                <img
                  src={`${staticPath("/static/images/new-grad-job/consultation/decoration-8.svg")}`}
                  className="decoration d8"
                />
              </Section>
              <Section>
                <img
                  src={`${staticPath("/static/images/new-grad-job/consultation/decoration-9.svg")}`}
                  className="decoration d9"
                />
                <Detail />
                <Pc>
                  <img
                    src={`${staticPath("/static/images/new-grad-job/consultation/decoration-10.svg")}`}
                    className="decoration d10"
                  />
                </Pc>
              </Section>
              <Section>
                <CvAppeal
                  onClick={() => {
                    userStore.authed ? applyConsultation() : uiStore.toggleRegistrationModal()
                  }}
                  isLoading={isLoading}
                  graduatesIn={userStore.user ? userStore.user.graduatesIn : undefined}
                />
                <Sp>
                  <img
                    src={`${staticPath("/static/images/new-grad-job/consultation/decoration-10.svg")}`}
                    className="decoration d10"
                  />
                </Sp>
              </Section>
              <ApplyDoneModal
                open={uiStore.completeApplyConsultationModalOpen}
                close={() => uiStore.toggleApplyConsultationModal()}
                popupAreaClassName="gtm_consultation-apply-done-modal"
                graduatesIn={userStore.user ? userStore.user.graduatesIn : undefined}
              />
              <Dialog
                open={uiStore.needRegistrationModalOpen}
                heading="登録またはログインが必要です"
                popupAreaClassName="gtm_consultation-register-modal"
                mainAction={{
                  className: "gtm_consultation-register-button",
                  label: "登録する",
                  action: () =>
                    window.open(
                      internalLink(
                        `/auth/register?redirect_after_auth=${location.pathname}?register-complete_from=consultation`
                      ),
                      "_self"
                    ),
                }}
                subAction={{
                  className: "gtm_consultation-cancel-button",
                  label: "キャンセル",
                  action: () => uiStore.toggleRegistrationModal(),
                }}
              >
                キャリア面談に参加するためには、新規登録またはログインが必要です。
              </Dialog>
            </Container>
          )}
        </Observer>
        <Footer />
      </>
    )
  }
}

const Container = styled.div`
  position: relative;
  padding: 0 0 96px;
  overflow: hidden;
  background: repeat-y url(${staticPath("/static/images/new-grad-job/consultation/background.png")});
  background-size: contain;
  ${IsMobile(css`
    padding: 0 0 64px;
  `)};
  .decoration {
    position: absolute;
    z-index: 1;
    &.d1 {
      top: 0;
      left: 0;
    }
  }
`
const Section = styled.section`
  position: relative;
  margin-top: 96px;
  z-index: 2;
  ${IsMobile(css`
    margin-top: 64px;
  `)};
  .decoration {
    position: absolute;
    z-index: -1;
    &.d2 {
      top: -24px;
      right: 0;
      z-index: 1;
      ${IsMobile(css`
        top: 269px;
        opacity: 0.6;
      `)};
    }
    &.d3 {
      bottom: -128px;
      left: 0;
      ${IsMobile(css`
        bottom: -152px;
        left: -32px;
      `)};
    }
    &.d4 {
      top: 58px;
      right: 0;
      ${IsMobile(css`
        top: 48px;
        right: -64px;
      `)};
    }
    &.d5 {
      bottom: -86px;
      left: 0;
      ${IsMobile(css`
        bottom: -296px;
        left: -44px;
      `)};
    }
    &.d6 {
      top: 140px;
      right: 0;
      ${IsMobile(css`
        top: 440px;
        right: -170px;
      `)};
    }
    &.d7 {
      top: -22px;
      left: 0;
      ${IsMobile(css`
        top: auto;
        bottom: -132px;
        left: -64px;
      `)};
    }
    &.d8 {
      top: 65px;
      right: 0;
      ${IsMobile(css`
        top: 40px;
        right: -108px;
      `)};
    }
    &.d9 {
      top: -12px;
      left: 0;
      ${IsMobile(css`
        top: -56px;
        left: -24px;
      `)};
    }
    &.d10 {
      bottom: -40px;
      right: 0;
      ${IsMobile(css`
        top: auto;
        bottom: -72px;
        right: -24px;
      `)};
    }
  }
`

// Common

interface HeadingProps {
  typoPath: string
  supplement: string
  heading: string
}

export const Heading = ({ typoPath, supplement, heading }: HeadingProps) => {
  return (
    <HeadingContainer>
      <img src={typoPath} className="img" />
      <div className="typo-wrapper">
        <Pc>
          <Typography type="Heading" colorProp={Color.Primary}>
            {supplement}
          </Typography>
        </Pc>
        <Sp>
          <Typography type="SubHeading" colorProp={Color.Primary}>
            {supplement}
          </Typography>
        </Sp>
        <Pc>
          <Typography type="Hero" colorProp={Color.Black} className="heading">
            {heading}
          </Typography>
        </Pc>
        <Sp>
          <Typography type="SubHero" colorProp={Color.Black} className="heading">
            {heading}
          </Typography>
        </Sp>
      </div>
    </HeadingContainer>
  )
}

const HeadingContainer = styled.div`
  ${IsMobile(css`
    text-align: center;
  `)};
  .img {
    height: 36px;
    ${IsMobile(css`
      height: 20px;
    `)};
  }
  .typo-wrapper {
    margin-top: ${SpacingSize.XL}px;
    text-align: center;
    &:after {
      display: block;
      content: "";
      height: 32px;
      width: 2px;
      background: ${Color.Primary};
      margin: ${SpacingSize.S}px auto auto;
      ${IsMobile(css`
        height: 24px;
      `)};
    }
    ${IsMobile(css`
      margin-top: 64px;
    `)};
  }
  .heading {
    margin-top: ${SpacingSize["3XS"]}px;
  }
`

// Section

interface MainVisualProps {
  onClick: Function
  isLoading?: boolean
  graduatesIn?: number
}

const MainVisual = ({ onClick, isLoading, graduatesIn }: MainVisualProps) => {
  return (
    <MainVisualContainer>
      <Pc>
        <img src={staticPath("/static/images/new-grad-job/consultation/student.png")} className="mv-img" />
      </Pc>
      <Sp>
        <img src={staticPath("/static/images/new-grad-job/consultation/student_sp.png")} className="mv-img" />
      </Sp>
      <LayoutContainer size="Large" className="inner">
        <img
          src={staticPath("/static/images/new-grad-job/consultation/typo-career-consultation.svg")}
          className="mv-typo"
        />
        <p className="hero">
          早期から選考に参加し、
          <br />
          行きたい会社に内定しよう！
        </p>
        <Pc>
          <Typography type="Heading" colorProp={Color.LightGray} className="supplement">
            就活対策や相談、厳選企業をご紹介！
            <br />
            {graduatesIn === 2023 ? <>23卒キャリア面談先行受付中</> : <>22卒向けキャリア面談</>}
          </Typography>
        </Pc>
        <Sp>
          <Typography type="SubHeading" colorProp={Color.LightGray} className="supplement">
            就活対策や相談、厳選企業をご紹介！
            <br />
            22卒向けキャリア面談
          </Typography>
        </Sp>
        <Button
          disabled
          size="Medium"
          onClick={onClick}
          isLoading={isLoading}
          className="button gtm_consultation-apply-button"
        >
          終了しました
        </Button>

        <div className="advisors-voice">
          <img
            src={staticPath("/static/images/new-grad-job/consultation/advisor.png")}
            className="advisors-voice_advisor"
          />
          <div className="advisors-voice_main">
            <img
              src={staticPath("/static/images/new-grad-job/consultation/typo-advisors-voice.svg")}
              className="advisors-voice_typo"
            />
            <div className="advisors-voice_text">
              <Pc>
                <Typography type="Heading" colorProp={Color.Black} className="advisors-voice_text-heading">
                  行きたい会社に内定してる人は、早期から選考に参加して
                  <span className="advisors-voice_text-dot">改</span>
                  <span className="advisors-voice_text-dot">善</span>してます！
                </Typography>
              </Pc>
              <Sp>
                <Typography type="Label" colorProp={Color.Black} className="advisors-voice_text-heading">
                  行きたい会社に内定してる人は、早期から選考に参加して
                  <span className="advisors-voice_text-dot">改</span>
                  <span className="advisors-voice_text-dot">善</span>してます！
                </Typography>
              </Sp>
              <Pc>
                <Typography type="Note" colorProp={Color.Gray} className="advisors-voice_text-supplemant">
                  就活は1回のエントリーで内定が出るほど簡単ではありません。自分の魅力を最大限伝えるには面接と改善の繰り返しが大切です。企業からのフィードバックを基に振り返りと改善をし、行きたい会社に内定しましょう！
                </Typography>
              </Pc>
              <Sp>
                <Typography type="SubNote" colorProp={Color.Gray} className="advisors-voice_text-supplemant">
                  就活は1回のエントリーで内定が出るほど簡単ではありません。自分の魅力を最大限伝えるには面接と改善の繰り返しが大切です。企業からのフィードバックを基に振り返りと改善をし、行きたい会社に内定しましょう！
                </Typography>
              </Sp>
            </div>
          </div>
        </div>
      </LayoutContainer>
    </MainVisualContainer>
  )
}

const MainVisualContainer = styled.div`
  position: relative;
  .inner {
    display: flex;
    height: 640px;
    align-items: center;
    ${IsMobile(css`
      height: auto;
      text-align: center;
    `)};
  }
  .mv-img {
    position: absolute;
    height: 640px;
    top: 0;
    left: 50%;
    border-radius: ${RadiusSize.Medium}px;
    ${IsMobile(css`
      height: 240px;
      top: 292px;
      left: auto;
      right: 96px;
    `)};
  }
  .mv-typo {
    height: 20px;
  }
  .hero {
    font-size: ${TextSize["3XL"]}px;
    font-weight: bold;
    line-height: ${LineHeight.S}%;
    color: ${Color.Black};
    margin-top: ${SpacingSize.S}px;
    ${IsMobile(css`
      font-size: 24px;
      text-align: center;
    `)}
  }
  .supplement {
    margin-top: ${SpacingSize.XS}px;
  }
  .button {
    max-width: 280px;
    width: 100%;
    margin-top: ${SpacingSize.XL}px;
    ${IsMobile(css`
      margin: 24px auto 0;
    `)};
  }
  .advisors-voice {
    position: relative;
    display: flex;
    align-items: flex-end;
    margin-top: 80px;
    z-index: ${Z.Child};
    ${IsMobile(css`
      margin-top: 184px;
      flex-direction: row-reverse;
      align-items: flex-start;
      text-align: left;
      max-width: 480px;
      margin-left: auto;
      margin-right: auto;
    `)};
  }
  .advisors-voice_typo {
    height: 36px;
    margin-left: ${SpacingSize.XS}px;
    ${IsMobile(css`
      height: 20px;
      margin-left: 0;
    `)};
  }
  .advisors-voice_advisor {
    height: 180px;
    width: 180px;
    border-radius: 50%;
    border: solid 8px ${Color.White};
    z-index: ${Z.Child};
    ${IsMobile(css`
      height: 80px;
      width: 80px;
      border: solid 4px #fff;
    `)};
  }
  .advisors-voice_main {
    margin-top: -18px;
  }
  .advisors-voice_text {
    background: ${Color.White};
    padding: ${SpacingSize.XL}px ${SpacingSize["3XL"]}px ${SpacingSize.XL}px calc(${SpacingSize.XL}px + 90px);
    border-radius: ${RadiusSize.Medium}px;
    margin-left: -90px;
    ${Shadow.Deep};
    margin-top: ${SpacingSize.M}px;
    &:after {
      position: absolute;
      content: "";
      top: 0;
      left: 0;
      width: 180px;
      height: 180px;
      border-radius: 50%;
      border: solid 8px ${Color.White};
      ${Shadow.Deep};
      z-index: -1;
      ${IsMobile(css`
        width: 80px;
        height: 80px;
        left: auto;
        right: 0;
      `)};
    }
    ${IsMobile(css`
      margin-right: -68px;
      margin-left: 0;
      padding: 20px 24px;
      width: calc(100vw - 44px);
      margin-top: 8px;
      max-width: 480px;
    `)};
  }
  .advisors-voice_text-heading {
    ${IsMobile(css`
      margin-right: 48px;
    `)};
  }
  .advisors-voice_text-supplemant {
    margin-top: ${SpacingSize["3XS"]}px;
    ${IsMobile(css`
      margin-top: 8px;
    `)};
  }
  .advisors-voice_text-dot {
    position: relative;
    &:before {
      position: absolute;
      top: -8px;
      left: 7px;
      content: "";
      height: 6px;
      width: 6px;
      border-radius: 50%;
      background: ${Color.Primary};
      ${IsMobile(css`
        height: 4px;
        width: 4px;
        top: -5px;
        left: 5px;
      `)};
    }
  }
`

const About = () => {
  return (
    <AboutContainer>
      <LayoutContainer size="Large" className="inner sp-container">
        <Heading
          typoPath={`${staticPath("/static/images/new-grad-job/consultation/typo-what-is.svg")}`}
          heading="キャリア面談って？"
          supplement="内定まで徹底サポート"
        />

        <div className="feature-repeat-contents">
          <ol className="feature-list">
            <li>
              <img
                className="feature-list-item_label"
                src={`${staticPath("/static/images/new-grad-job/consultation/label-01.svg")}`}
              />
              <div className="feature-list-item_text">
                <Pc>
                  <Typography
                    type="SubHero"
                    lineHeightProp={160}
                    colorProp={Color.Black}
                    className="feature-list-item_text-heading"
                  >
                    キャリア相談
                    <br className="sm-hide" />
                    できる
                  </Typography>
                </Pc>
                <Sp>
                  <Typography
                    type="Heading"
                    lineHeightProp={160}
                    colorProp={Color.Black}
                    className="feature-list-item_text-heading"
                  >
                    キャリア相談
                    <br className="sm-hide" />
                    できる
                  </Typography>
                </Sp>
                <div className="feature-list-item_text-detail">
                  <Pc>
                    <Typography type="Heading" colorProp={Color.Black}>
                      経験豊富なアドバイザーが長期視点での企業選びをお手伝い
                    </Typography>
                  </Pc>
                  <Sp>
                    <Typography type="Label" colorProp={Color.Black}>
                      経験豊富なアドバイザーが長期視点での企業選びをお手伝い
                    </Typography>
                  </Sp>
                  <Pc>
                    <Typography type="Note" colorProp={Color.Gray} className="feature-list-item_text-detail-supplement">
                      長期インターン参加や就職活動を支援をしてきたアドバイザーが、就職の方向性や次のアクションを一緒に考えます。
                    </Typography>
                  </Pc>
                  <Sp>
                    <Typography
                      type="SubNote"
                      colorProp={Color.Gray}
                      className="feature-list-item_text-detail-supplement"
                    >
                      長期インターン参加や就職活動を支援をしてきたアドバイザーが、就職の方向性や次のアクションを一緒に考えます。
                    </Typography>
                  </Sp>
                </div>
              </div>
            </li>
            <li>
              <img
                className="feature-list-item_label"
                src={`${staticPath("/static/images/new-grad-job/consultation/label-02.svg")}`}
              />
              <div className="feature-list-item_text">
                <Pc>
                  <Typography
                    type="SubHero"
                    lineHeightProp={160}
                    colorProp={Color.Black}
                    className="feature-list-item_text-heading"
                  >
                    面接準備
                    <br className="sm-hide" />
                    できる
                  </Typography>
                </Pc>
                <Sp>
                  <Typography
                    type="Heading"
                    lineHeightProp={160}
                    colorProp={Color.Black}
                    className="feature-list-item_text-heading"
                  >
                    面接準備
                    <br className="sm-hide" />
                    できる
                  </Typography>
                </Sp>
                <div className="feature-list-item_text-detail">
                  <Pc>
                    <Typography type="Heading" colorProp={Color.Black}>
                      1対1でES添削や面接練習など、受かるための就活対策
                    </Typography>
                  </Pc>
                  <Sp>
                    <Typography type="Label" colorProp={Color.Black}>
                      1対1でES添削や面接練習など、受かるための就活対策
                    </Typography>
                  </Sp>
                  <Pc>
                    <Typography type="Note" colorProp={Color.Gray} className="feature-list-item_text-detail-supplement">
                      あなたの経験や強み、指向性などが企業に魅力的に伝わるように、ESや面接などの就活対策を一緒に行います。
                    </Typography>
                  </Pc>
                  <Sp>
                    <Typography
                      type="SubNote"
                      colorProp={Color.Gray}
                      className="feature-list-item_text-detail-supplement"
                    >
                      あなたの経験や強み、指向性などが企業に魅力的に伝わるように、ESや面接などの就活対策を一緒に行います。
                    </Typography>
                  </Sp>
                </div>
              </div>
            </li>
            <li className="label">
              <img
                className="feature-list-item_label"
                src={`${staticPath("/static/images/new-grad-job/consultation/label-03.svg")}`}
              />
              <div className="feature-list-item_text">
                <Pc>
                  <Typography
                    type="SubHero"
                    lineHeightProp={160}
                    colorProp={Color.Black}
                    className="feature-list-item_text-heading"
                  >
                    選考に参加
                    <br className="sm-hide" />
                    できる
                  </Typography>
                </Pc>
                <Sp>
                  <Typography
                    type="Heading"
                    lineHeightProp={160}
                    colorProp={Color.Black}
                    className="feature-list-item_text-heading"
                  >
                    選考に参加
                    <br className="sm-hide" />
                    できる
                  </Typography>
                </Sp>
                <div className="feature-list-item_text-detail">
                  <Pc>
                    <Typography type="Heading" colorProp={Color.Black}>
                      早期から本選考を開始している優良企業に挑戦
                    </Typography>
                  </Pc>
                  <Sp>
                    <Typography type="Label" colorProp={Color.Black}>
                      早期から本選考を開始している優良企業に挑戦
                    </Typography>
                  </Sp>
                  <Pc>
                    <Typography type="Note" colorProp={Color.Gray} className="feature-list-item_text-detail-supplement">
                      インターンや学生団体などの経験を評価する、早期選考中の企業をご紹介します。早くに動き経験を積むことで、早期内定を獲得できます。
                    </Typography>
                  </Pc>
                  <Sp>
                    <Typography
                      type="SubNote"
                      colorProp={Color.Gray}
                      className="feature-list-item_text-detail-supplement"
                    >
                      インターンや学生団体などの経験を評価する、早期選考中の企業をご紹介します。早くに動き経験を積むことで、早期内定を獲得できます。
                    </Typography>
                  </Sp>
                </div>
              </div>
              <div className="feature-list-item_batch">
                <Pc>
                  <Typography type="SubHeading" colorProp={Color.White} className="feature-list-item_batch-text">
                    早期内定
                    <br />
                    獲得
                  </Typography>
                </Pc>
                <Sp>
                  <Typography type="SubLabel" colorProp={Color.White} className="feature-list-item_batch-text">
                    早期内定
                    <br />
                    獲得
                  </Typography>
                </Sp>
              </div>
            </li>
            <li>
              <img
                className="feature-list-item_label"
                src={`${staticPath("/static/images/new-grad-job/consultation/label-04.svg")}`}
              />
              <div className="feature-list-item_text">
                <Pc>
                  <Typography
                    type="SubHero"
                    lineHeightProp={160}
                    colorProp={Color.Black}
                    className="feature-list-item_text-heading"
                  >
                    改善
                    <br className="sm-hide" />
                    できる
                  </Typography>
                </Pc>
                <Sp>
                  <Typography
                    type="Heading"
                    lineHeightProp={160}
                    colorProp={Color.Black}
                    className="feature-list-item_text-heading"
                  >
                    改善
                    <br className="sm-hide" />
                    できる
                  </Typography>
                </Sp>
                <div className="feature-list-item_text-detail">
                  <Pc>
                    <Typography type="Heading" colorProp={Color.Black}>
                      受けた企業から直接フィードバックが貰える
                    </Typography>
                  </Pc>
                  <Sp>
                    <Typography type="Label" colorProp={Color.Black}>
                      受けた企業から直接フィードバックが貰える
                    </Typography>
                  </Sp>
                  <Pc>
                    <Typography type="Note" colorProp={Color.Gray} className="feature-list-item_text-detail-supplement">
                      ESや面接の内容を企業からフィードバックが貰えます。フィードバックを基にアドバイザーと振り返ることで、次の選考に活かせます。
                    </Typography>
                  </Pc>
                  <Sp>
                    <Typography
                      type="SubNote"
                      colorProp={Color.Gray}
                      className="feature-list-item_text-detail-supplement"
                    >
                      ESや面接の内容を企業からフィードバックが貰えます。フィードバックを基にアドバイザーと振り返ることで、次の選考に活かせます。
                    </Typography>
                  </Sp>
                </div>
              </div>
            </li>
          </ol>
          <div className="repeat-shape-wrapper">
            <div className="repeat-shape">
              <img
                src={`${staticPath("/static/images/new-grad-job/consultation/triangle-top.svg")}`}
                className="repeat-shape_triangle top"
              />
              <div className="repeat-shape_round">
                <div className="repeat-shape_round-inner">
                  <Icon type="Repeat" size={24} color={Color.Secondary} />
                  <div className="repeat-shape_round-text">繰り返す</div>
                </div>
              </div>
              <img
                src={`${staticPath("/static/images/new-grad-job/consultation/triangle-bottom.svg")}`}
                className="repeat-shape_triangle bottom"
              />
            </div>
          </div>
        </div>
        <div className="consultation-goal">
          <div className="consultation-goal_clip" />
          <Pc>
            <Typography type="SubHero">
              あなたに合った、<span className="consultation-goal_dot">行</span>
              <span className="consultation-goal_dot">き</span>
              <span className="consultation-goal_dot">た</span>
              <span className="consultation-goal_dot">い</span>企業に
              <span className="consultation-goal_line">内定</span>
            </Typography>
          </Pc>
          <Sp>
            <Typography type="Heading">
              あなたに合った、
              <br />
              <span className="consultation-goal_dot">行</span>
              <span className="consultation-goal_dot">き</span>
              <span className="consultation-goal_dot">た</span>
              <span className="consultation-goal_dot">い</span>企業に
              <span className="consultation-goal_line">内定</span>
            </Typography>
          </Sp>
        </div>
      </LayoutContainer>
    </AboutContainer>
  )
}

const AboutContainer = styled.div`
  .sp-container {
    ${css`
      ${IsMobile(`
        max-width: 480px;
        margin: auto;
      `)}
    `}
  }
  .feature-repeat-contents {
    display: flex;
    align-items: center;
    margin-top: ${SpacingSize.XL}px;
  }
  .feature-list {
    width: calc(100% - 84px);
    ${IsMobile(css`
      width: calc(100% - 24px);
    `)};
    li {
      position: relative;
      background: ${Color.White};
      padding: ${SpacingSize.XL}px ${SpacingSize["2XL"]}px ${SpacingSize.XL}px ${SpacingSize["5XL"]}px;
      border-radius: ${RadiusSize.Medium}px;
      ${Shadow.Deep};
      ${IsMobile(css`
        padding: 16px 24px;
        min-height: 179px;
      `)};
      &.label {
        &:before {
          position: absolute;
          content: "";
          top: -16px;
          right: -16px;
          z-index: -1;
          ${Shadow.Deep};
          width: 80px;
          height: 80px;
          border-radius: 50%;
          ${IsMobile(css`
            width: 64px;
            height: 64px;
          `)};
        }
      }
      &:not(:first-child) {
        margin-top: ${SpacingSize.XL}px;
      }
    }
  }
  .feature-list-item_label {
    position: absolute;
    top: 0;
    left: 0;
    height: 48px;
  }
  .feature-list-item_text {
    display: flex;
    ${IsMobile(css`
      flex-direction: column;
    `)};
  }
  .feature-list-item_text-heading {
    min-width: 184px;
    ${IsMobile(css`
      margin-left: 32px;
      .sm-hide {
        display: none;
      }
    `)};
  }
  .feature-list-item_text-detail {
    margin-left: ${SpacingSize.XL}px;
    ${IsMobile(css`
      margin-top: 16px;
      margin-left: 0;
    `)};
  }
  .feature-list-item_text-detail-supplement {
    margin-top: ${SpacingSize["3XS"]}px;
  }
  .feature-list-item_batch {
    position: absolute;
    top: -16px;
    right: -16px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 80px;
    height: 80px;
    background: ${Color.Attention};
    border-radius: 50%;
    border: solid 4px ${Color.White};
    transform: rotate(16deg);
    ${IsMobile(css`
      width: 64px;
      height: 64px;
    `)};
  }
  .feature-list-item_batch-text {
    margin-top: ${SpacingSize["2XS"]}px;
  }
  .repeat-shape-wrapper {
    position: relative;
    margin-left: 20px;
    width: 64px;
    display: flex;
    justify-content: center;
    ${IsMobile(css`
      width: 32px;
      margin-left: 4px;
      margin-right: -12px;
    `)};
  }
  .repeat-shape {
    display: flex;
    align-items: center;
    width: 6px;
    height: 560px;
    ${Shadow.Deep};
    background: #fff;
    ${IsMobile(css`
      width: 4px;
      height: 780px;
    `)};
    &:before {
      position: absolute;
      content: "";
      top: 6px;
      left: calc(50% - 3px);
      width: 6px;
      height: 12px;
      background: #fff;
      z-index: 1;
      ${IsMobile(css`
        width: 4px;
        left: calc(50% - 2px);
      `)};
    }
    &:after {
      position: absolute;
      content: "";
      bottom: 6px;
      left: calc(50% - 3px);
      width: 6px;
      height: 12px;
      background: #fff;
      z-index: 1;
      ${IsMobile(css`
        width: 4px;
        left: calc(50% - 2px);
      `)};
    }
  }
  .repeat-shape_round {
    position: absolute;
    top: calc(50% - 32px);
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${Color.White};
    ${Shadow.Deep};
    ${IsMobile(css`
      width: 32px;
      height: 32px;
      margin-top: 6px;
    `)};
    &:before {
      position: absolute;
      content: "";
      width: 6px;
      height: 24px;
      background: ${Color.White};
      top: -12px;
      left: calc(50% - 3px);
      ${IsMobile(css`
        width: 4px;
        height: 16px;
        left: calc(50% - 2px);
      `)};
    }
    &:after {
      position: absolute;
      content: "";
      width: 6px;
      height: 24px;
      background: ${Color.White};
      bottom: -12px;
      left: calc(50% - 3px);
      ${IsMobile(css`
        width: 4px;
        height: 16px;
        left: calc(50% - 2px);
      `)};
    }
  }
  .repeat-shape_triangle {
    position: absolute;
    &.top {
      top: -16px;
      left: calc(50% - 24px);
    }
    &.bottom {
      bottom: -20px;
      left: calc(50% - 24px);
    }
  }
  .repeat-shape_round-inner {
    text-align: center;
  }
  .repeat-shape_round-text {
    font-size: ${TextSize["2XS"]}px;
    font-weight: bold;
    color: ${Color.Secondary};
    margin-top: ${SpacingSize["4XS"]}px;
    line-height: 100%;
    ${IsMobile(css`
      display: none;
    `)};
  }
  .consultation-goal {
    position: relative;
    padding: ${SpacingSize.XL}px ${SpacingSize["2XL"]}px;
    border-radius: ${RadiusSize.Medium}px;
    ${Shadow.Deep};
    text-align: center;
    margin-top: ${SpacingSize["XL"]}px;
    background: ${Color.White};
    ${IsMobile(css`
      padding: 16px 24px;
    `)};
  }
  .consultation-goal_dot {
    position: relative;
    &:before {
      position: absolute;
      top: -8px;
      left: 9px;
      content: "";
      height: 6px;
      width: 6px;
      border-radius: 50%;
      background: ${Color.Primary};
      ${IsMobile(css`
        top: -5px;
        left: 8px;
        height: 4px;
        width: 4px;
      `)};
    }
  }
  .consultation-goal_line {
    position: relative;
    &:before {
      position: absolute;
      bottom: -8px;
      left: 0px;
      content: "";
      height: 4px;
      width: 100%;
      background: ${Color.Attention};
      ${IsMobile(css`
        bottom: -5px;
        height: 2px;
      `)};
    }
  }
  .consultation-goal_clip {
    position: absolute;
    width: 20px;
    height: 20px;
    top: -10px;
    left: calc(50% - 14px);
    background: #f7f7f7;
    box-shadow: 0 1px 6px rgba(33, 33, 33, 0.1) inset;
    transform: rotate(135deg);
    &:before {
      position: absolute;
      top: 8px;
      left: -9px;
      content: "";
      background: #f7f7f7;
      width: 28px;
      height: 15px;
      transform: rotate(45deg);
    }
  }
`

const Recommend = () => {
  return (
    <RecommendContainer>
      <LayoutContainer size="Large" className="sp-container">
        <Heading
          typoPath={`${staticPath("/static/images/new-grad-job/consultation/typo-recommend.svg")}`}
          heading="こんな方は特におすすめ"
          supplement={`1つでも当てはまったら参加しよう`}
        />
        <ul className="recommend_list">
          <li>
            <img
              src={`${staticPath("/static/images/new-grad-job/consultation/recommend-experience.png")}`}
              className="recommend_list-item-img"
            />
            <Pc>
              <Typography type="Heading" colorProp={Color.Black} className="recommend_list-item-heading">
                これまでの経験を活かして
                <br />
                就活したい
              </Typography>
              <Typography type="Note" colorProp={Color.Gray} className="recommend_list-item-supplement">
                経験を魅力的に伝えるノウハウだけでなく、
                <br />
                経験を評価する企業が集まっています。
              </Typography>
            </Pc>
            <Sp>
              <Typography type="SubHeading" colorProp={Color.Black} className="recommend_list-item-heading">
                これまでの経験を活かして
                <br />
                就活したい
              </Typography>
              <Typography type="SubNote" colorProp={Color.Gray} className="recommend_list-item-supplement">
                経験を魅力的に伝えるノウハウだけでなく、
                <br />
                経験を評価する企業が集まっています。
              </Typography>
            </Sp>
          </li>
          <li>
            <img
              src={`${staticPath("/static/images/new-grad-job/consultation/recommend-selection.png")}`}
              className="recommend_list-item-img"
            />
            <Pc>
              <Typography type="Heading" colorProp={Color.Black} className="recommend_list-item-heading">
                早期選考に参加し就活対策したい
                <br />
                &内定獲得したい
              </Typography>
              <Typography type="Note" colorProp={Color.Gray} className="recommend_list-item-supplement">
                面接経験や内定を掴みましょう。早期に動くことで心に余裕ができ、有利に就活を進められます。
              </Typography>
            </Pc>
            <Sp>
              <Typography type="SubHeading" colorProp={Color.Black} className="recommend_list-item-heading">
                早期選考に参加し就活対策したい
                <br />
                &内定獲得したい
              </Typography>
              <Typography type="SubNote" colorProp={Color.Gray} className="recommend_list-item-supplement">
                面接経験や内定を掴みましょう。早期に動くことで
                <br />
                心に余裕ができ、有利に就活を進められます。
              </Typography>
            </Sp>
          </li>
          <li>
            <img
              src={`${staticPath("/static/images/new-grad-job/consultation/recommend-company.png")}`}
              className="recommend_list-item-img"
            />
            <Pc>
              <Typography type="Heading" colorProp={Color.Black} className="recommend_list-item-heading">
                学生にはあまり知られていない
                <br />
                優良企業を知りたい
              </Typography>
              <Typography type="Note" colorProp={Color.Gray} className="recommend_list-item-supplement">
                誰もが知ってる有名企業の他にも、業界で活躍している優良企業はたくさんあります！
              </Typography>
            </Pc>
            <Sp>
              <Typography type="SubHeading" colorProp={Color.Black} className="recommend_list-item-heading">
                学生にはあまり知られていない
                <br />
                優良企業を知りたい
              </Typography>
              <Typography type="SubNote" colorProp={Color.Gray} className="recommend_list-item-supplement">
                誰もが知ってる有名企業の他にも、
                <br />
                業界で活躍している優良企業はたくさんあります！
              </Typography>
            </Sp>
          </li>
        </ul>
      </LayoutContainer>
    </RecommendContainer>
  )
}

const RecommendContainer = styled.div`
  .sp-container {
    ${css`
      ${IsMobile(`
        max-width: 480px;
        margin: auto;
      `)}
    `}
  }
  .recommend_list {
    display: flex;
    margin-top: ${SpacingSize.XL}px;
    ${IsMobile(css`
      flex-direction: column;
    `)};
    li {
      width: 33.33%;
      text-align: center;
      &:not(:first-child) {
        margin-left: ${SpacingSize["2XL"]}px;
      }
      ${IsMobile(css`
        width: 100%;
        &:not(:first-child) {
          margin-left: 0;
          margin-top: 48px;
        }
      `)};
    }
  }
  .recommend_list-item-img {
    height: 120px;
    border-radius: 50%;
    ${Shadow.Deep};
  }
  .recommend_list-item-heading {
    margin-top: ${SpacingSize.XL}px;
    ${IsMobile(css`
      margin-top: 16px;
    `)};
  }
  .recommend_list-item-supplement {
    margin-top: ${SpacingSize.XS}px;
    ${IsMobile(css`
      margin-top: 4px;
    `)};
  }
`

interface CvAppealProps {
  onClick: Function
  isLoading?: boolean
  graduatesIn?: number
}

const CvAppeal = ({ onClick, isLoading, graduatesIn }: CvAppealProps) => {
  return (
    <CvAppealContainer>
      <LayoutContainer size="Large" className="sp-container">
        <div className="cv-appeal_card">
          <div className="cv-appeal_card-message">
            <Pc>
              <Typography type="SubHero" colorProp={Color.Black}>
                さぁ、さっそく内定に向けて
                <br />
                1歩を踏み出そう
              </Typography>
              <Typography type="Note" colorProp={Color.Gray} className="cv-appeal_card-message-supplement">
                完全無料で1時間、1対1のオンライン面談です。
                <br />
                少しでも気になった方は気軽に申し込みください！
              </Typography>
            </Pc>
            <Sp>
              <Typography type="Heading" colorProp={Color.Black}>
                さぁ、さっそく内定に向けて
                <br />
                1歩を踏み出そう
              </Typography>
              <Typography type="SubNote" colorProp={Color.Gray} className="cv-appeal_card-message-supplement">
                完全無料で1時間、1対1でのオンライン面談です。
                <br />
                少しでも気になった方は気軽に申し込みください！
              </Typography>
            </Sp>
            <Button
              disabled
              size="Medium"
              onClick={onClick}
              className="cv-appeal_card-message-button gtm_consultation-apply-button"
              isLoading={isLoading}
            >
              終了しました
            </Button>
          </div>
          <img
            src={`${staticPath("/static/images/new-grad-job/consultation/consultation.png")}`}
            className="cv-appeal_card-img"
          />
        </div>
      </LayoutContainer>
    </CvAppealContainer>
  )
}

const CvAppealContainer = styled.div`
  .sp-container {
    ${css`
      ${IsMobile(`
        max-width: 480px;
        margin: auto;
      `)}
    `}
  }
  .cv-appeal_card {
    background: ${Color.White};
    display: flex;
    align-items: flex-start;
    ${Shadow.Deep};
    border-radius: ${RadiusSize.Medium}px;
    overflow: hidden;
    ${IsMobile(css`
      flex-direction: column-reverse;
    `)};
  }
  .cv-appeal_card-message {
    display: flex;
    justify-content: center;
    flex-direction: column;
    width: 50%;
    padding: ${SpacingSize.XL}px ${SpacingSize.XL}px ${SpacingSize.XL}px ${SpacingSize["2XL"]}px;
    ${IsMobile(css`
      width: 100%;
      padding: 16px 24px 24px;
      text-align: center;
    `)};
  }
  .cv-appeal_card-message-supplement {
    margin-top: ${SpacingSize["3XS"]}px;
    ${IsMobile(css`
      margin-top: 8px;
    `)};
  }
  .cv-appeal_card-message-button {
    margin-top: ${SpacingSize.M}px;
    max-width: 280px;
    ${IsMobile(css`
      margin-left: auto;
      margin-right: auto;
    `)};
  }
  .cv-appeal_card-img {
    width: 50%;
    object-fit: cover;
    ${IsMobile(css`
      width: 100%;
    `)};
  }
`

const Flow = () => {
  return (
    <FlowContainer>
      <LayoutContainer size="Large" className="sp-container">
        <Heading
          typoPath={`${staticPath("/static/images/new-grad-job/consultation/typo-flow.svg")}`}
          heading="内定までの流れ"
          supplement="すべて1対1で丁寧な対応"
        />
        <div className="flow-content">
          <ol className="flow-content_line first">
            <li>
              <Pc>
                <Typography type="Heading" colorProp={Color.Black} className="flow-content_card-heading">
                  Infraに登録&申し込み
                </Typography>
                <Typography type="Note" colorProp={Color.Gray} className="flow-content_card-supplement">
                  相談会の申し込みには登録が必要です。登録して早速申し込みましょう！
                </Typography>
              </Pc>
              <Sp>
                <Typography type="SubHeading" colorProp={Color.Black} className="flow-content_card-heading">
                  Infraに登録&申し込み
                </Typography>
                <Typography type="SubNote" colorProp={Color.Gray} className="flow-content_card-supplement">
                  相談会の申し込みには登録が必要です。登録して早速申し込みましょう！
                </Typography>
              </Sp>
            </li>
            <li>
              <Pc>
                <Typography type="Heading" colorProp={Color.Black} className="flow-content_card-heading">
                  日程調整&ES準備
                </Typography>
                <Typography type="Note" colorProp={Color.Gray} className="flow-content_card-supplement">
                  LINEで日程を決めます。ESをお渡しするので、面談までにご記入ください。
                </Typography>
              </Pc>
              <Sp>
                <Typography type="SubHeading" colorProp={Color.Black} className="flow-content_card-heading">
                  日程調整&ES準備
                </Typography>
                <Typography type="SubNote" colorProp={Color.Gray} className="flow-content_card-supplement">
                  LINEで日程を決めます。ESをお渡しするので、面談までにご記入ください。
                </Typography>
              </Sp>
            </li>
            <li>
              <Pc>
                <Typography type="Heading" colorProp={Color.Black} className="flow-content_card-heading">
                  キャリアアドバイザーと
                  <br />
                  オンライン面談
                </Typography>
                <Typography type="Note" colorProp={Color.Gray} className="flow-content_card-supplement">
                  無料ビデオ通話ツールで1時間面談します。相談内容を考えておきましょう。
                </Typography>
              </Pc>
              <Sp>
                <Typography type="SubHeading" colorProp={Color.Black} className="flow-content_card-heading">
                  アドバイザーとオンライン面談
                </Typography>
                <Typography type="SubNote" colorProp={Color.Gray} className="flow-content_card-supplement">
                  無料ビデオ通話ツールで1時間面談します。相談内容を考えておきましょう。
                </Typography>
              </Sp>
            </li>
          </ol>
          <ol className="flow-content_line second">
            <li>
              <Pc>
                <Typography type="Heading" colorProp={Color.Black} className="flow-content_card-heading">
                  ES添削&
                  <br />
                  企業にエントリー
                </Typography>
                <Typography type="Note" colorProp={Color.Gray} className="flow-content_card-supplement">
                  あなたの魅力が最大限伝わるようESを添削してから、エントリーします。
                </Typography>
              </Pc>
              <Sp>
                <Typography type="SubHeading" colorProp={Color.Black} className="flow-content_card-heading">
                  ES添削&企業にエントリー
                </Typography>
                <Typography type="SubNote" colorProp={Color.Gray} className="flow-content_card-supplement">
                  あなたの魅力が最大限伝わるようESを添削してから、エントリーします。
                </Typography>
              </Sp>
            </li>
            <li>
              <Pc>
                <Typography type="Heading" colorProp={Color.Black} className="flow-content_card-heading">
                  選考
                </Typography>
                <Typography type="Note" colorProp={Color.Gray} className="flow-content_card-supplement">
                  準備してきたことを生かして、面接に臨みましょう。
                </Typography>
              </Pc>
              <Sp>
                <Typography type="SubHeading" colorProp={Color.Black} className="flow-content_card-heading">
                  選考
                </Typography>
                <Typography type="SubNote" colorProp={Color.Gray} className="flow-content_card-supplement">
                  準備してきたことを生かして、面接に臨みましょう。
                </Typography>
              </Sp>
            </li>
            <li>
              <Pc>
                <Typography type="Heading" colorProp={Color.Black} className="flow-content_card-heading">
                  オンライン面談で
                  <br />
                  振り返り&改善
                </Typography>
                <Typography type="Note" colorProp={Color.Gray} className="flow-content_card-supplement">
                  企業からのフィードバックを基に振り返りを行い、改善します。
                </Typography>
              </Pc>
              <Sp>
                <Typography type="SubHeading" colorProp={Color.Black} className="flow-content_card-heading">
                  オンライン面談で振り返り&改善
                </Typography>
                <Typography type="SubNote" colorProp={Color.Gray} className="flow-content_card-supplement">
                  企業からのフィードバックを基に振り返りを行い、改善します。
                </Typography>
              </Sp>
            </li>
            <li>
              <Pc>
                <Typography type="Heading" colorProp={Color.Black} className="flow-content_card-heading">
                  行きたい企業に
                  <br />
                  内定
                </Typography>
                <Typography type="Note" colorProp={Color.Gray} className="flow-content_card-supplement">
                  おめでとうございます！ ご活躍を期待しております！
                </Typography>
              </Pc>
              <Sp>
                <Typography type="SubHeading" colorProp={Color.Black} className="flow-content_card-heading">
                  行きたい企業に内定
                </Typography>
                <Typography type="SubNote" colorProp={Color.Gray} className="flow-content_card-supplement">
                  おめでとうございます！ ご活躍を期待しております！
                </Typography>
              </Sp>
            </li>
          </ol>
        </div>
      </LayoutContainer>
    </FlowContainer>
  )
}

const FlowContainer = styled.div`
  .sp-container {
    ${css`
      ${IsMobile(`
        max-width: 480px;
        margin: auto;
      `)}
    `}
  }
  .flow-content {
    margin-top: ${SpacingSize.XL}px;
  }
  .flow-content_line {
    display: flex;
    &:not(:first-child) {
      margin-top: ${SpacingSize["2XL"]}px;
      ${IsMobile(css`
        margin-top: 24px;
      `)};
    }
    ${IsMobile(css`
      flex-direction: column;
    `)};
    li {
      position: relative;
      background: ${Color.White};
      padding: ${SpacingSize.XL}px ${SpacingSize["2XL"]}px;
      border-radius: ${RadiusSize.Medium}px;
      ${Shadow.Deep};
      min-height: 100px;
      ${IsMobile(css`
        padding: 16px 24px;
      `)};
      &:not(:first-child) {
        margin-left: ${SpacingSize["2XL"]}px;
        ${IsMobile(css`
          margin-left: 0;
          margin-top: 24px;
        `)};
      }
    }

    /* ここ */

    &.first {
      li {
        width: 33.33%;
        &:after {
          position: absolute;
          content: url(${staticPath("/static/images/new-grad-job/consultation/arrow-right.svg")});
          right: -44px;
          top: calc(50% - 25px);
          z-index: 1;
        }
        ${css`
          ${IsMobile(`
            width: calc(100% - 24px);
            &:after {
              content: url(${staticPath("/static/images/new-grad-job/consultation/arrow-bottom.svg")});
              top: auto;
              bottom: -40px;
              left: calc(50% - 24px);
            }
          `)}
        `}
      }
    }
    &.second {
      li {
        width: 25%;
        &:before {
          position: absolute;
          content: url(${staticPath("/static/images/new-grad-job/consultation/arrow-right.svg")});
          top: 0;
          left: -44px;
          top: calc(50% - 25px);
          z-index: 1;
        }
        ${css`
          ${IsMobile(`
            width: 100%;
            &:before {
              content: url(${staticPath("/static/images/new-grad-job/consultation/arrow-bottom.svg")});
              top: auto;
              bottom: -40px;
              left: calc(50% - 24px);
            }
            &:last-child {
              &:before {
                content: "";
              }
            }
          `)}
        `}
        &:first-child {
          &:after {
            position: absolute;
            content: url(${staticPath("/static/images/new-grad-job/consultation/arrow-back.svg")});
            left: calc(50% - 24px);
            bottom: -48px;
            z-index: 1;
            ${css`
              ${IsMobile(`
                content: url(${staticPath("/static/images/new-grad-job/consultation/arrow-back_sp.svg")});
                left: auto;
                right: -40px;
                bottom: auto;
                top: calc(50% - 24px);
              `)}
            `}
          }
        }
        ${IsMobile(css`
          width: calc(100% - 24px);
        `)};
      }
    }

    /* ここ */
  }
  .flow-content_card-heading {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    ${IsMobile(css`
      height: auto;
    `)};
  }
  .flow-content_card-supplement {
    margin-top: ${SpacingSize.M}px;
    ${css`
      ${IsMobile(`
        margin-top: ${SpacingSize.XS}px;
      `)}
    `}
  }
`

const Detail = () => {
  return (
    <DetailContainer>
      <LayoutContainer size="Large">
        <Heading
          typoPath={`${staticPath("/static/images/new-grad-job/consultation/typo-detail.svg")}`}
          heading="詳細な内容"
          supplement="完全無料のオンラインキャリア面談"
        />
      </LayoutContainer>
      <LayoutContainer size="Medium" className="sp-container">
        <ul className="detail-content_list">
          <li>
            <Icon type="User" color={Color.Secondary} size={48} />
            <Pc>
              <Typography type="Heading" colorProp={Color.Black} className="detail-content_list-heading">
                対象
              </Typography>
              <Typography type="Note" colorProp={Color.Gray} className="detail-content_list-supplement">
                22卒で
                <br />
                就職活動中の学生
              </Typography>
            </Pc>
            <Sp>
              <Typography type="SubHeading" colorProp={Color.Black} className="detail-content_list-heading">
                対象
              </Typography>
              <Typography type="SubNote" colorProp={Color.Gray} className="detail-content_list-supplement">
                22卒で
                <br />
                就職活動中の学生
              </Typography>
            </Sp>
          </li>
          <li>
            <Icon type="Area" color={Color.Secondary} size={48} />
            <Pc>
              <Typography type="Heading" colorProp={Color.Black} className="detail-content_list-heading">
                面談場所
              </Typography>
              <Typography type="Note" colorProp={Color.Gray} className="detail-content_list-supplement">
                1対1で
                <br />
                オンライン
              </Typography>
            </Pc>
            <Sp>
              <Typography type="SubHeading" colorProp={Color.Black} className="detail-content_list-heading">
                面談場所
              </Typography>
              <Typography type="SubNote" colorProp={Color.Gray} className="detail-content_list-supplement">
                1対1で
                <br />
                オンライン
              </Typography>
            </Sp>
          </li>
          <li>
            <Icon type="Money" color={Color.Secondary} size={48} />
            <Pc>
              <Typography type="Heading" colorProp={Color.Black} className="detail-content_list-heading">
                参加費
              </Typography>
              <Typography type="Note" colorProp={Color.Gray} className="detail-content_list-supplement">
                無料
              </Typography>
            </Pc>
            <Sp>
              <Typography type="SubHeading" colorProp={Color.Black} className="detail-content_list-heading">
                参加費
              </Typography>
              <Typography type="SubNote" colorProp={Color.Gray} className="detail-content_list-supplement">
                無料
              </Typography>
            </Sp>
          </li>
          <li>
            <Icon type="Clock" color={Color.Secondary} size={48} />
            <Pc>
              <Typography type="Heading" colorProp={Color.Black} className="detail-content_list-heading">
                所要時間
              </Typography>
              <Typography type="Note" colorProp={Color.Gray} className="detail-content_list-supplement">
                1時間程度
              </Typography>
            </Pc>
            <Sp>
              <Typography type="SubHeading" colorProp={Color.Black} className="detail-content_list-heading">
                所要時間
              </Typography>
              <Typography type="SubNote" colorProp={Color.Gray} className="detail-content_list-supplement">
                1時間程度
              </Typography>
            </Sp>
          </li>
          <li>
            <Icon type="Bag" color={Color.Secondary} size={48} />
            <Pc>
              <Typography type="Heading" colorProp={Color.Black} className="detail-content_list-heading">
                持ち物
              </Typography>
              <Typography type="Note" colorProp={Color.Gray} className="detail-content_list-supplement">
                InfraES
                <br />
                予約確定時にお渡し
              </Typography>
            </Pc>
            <Sp>
              <Typography type="SubHeading" colorProp={Color.Black} className="detail-content_list-heading">
                持ち物
              </Typography>
              <Typography type="SubNote" colorProp={Color.Gray} className="detail-content_list-supplement">
                InfraES
                <br />
                予約確定時にお渡し
              </Typography>
            </Sp>
          </li>
          <li>
            <Icon type="Shirt" color={Color.Secondary} size={48} />
            <Pc>
              <Typography type="Heading" colorProp={Color.Black} className="detail-content_list-heading">
                服装
              </Typography>
              <Typography type="Note" colorProp={Color.Gray} className="detail-content_list-supplement">
                自由
              </Typography>
            </Pc>
            <Sp>
              <Typography type="SubHeading" colorProp={Color.Black} className="detail-content_list-heading">
                服装
              </Typography>
              <Typography type="SubNote" colorProp={Color.Gray} className="detail-content_list-supplement">
                自由
              </Typography>
            </Sp>
          </li>
        </ul>
      </LayoutContainer>
    </DetailContainer>
  )
}

const DetailContainer = styled.div`
  .sp-container {
    ${css`
      ${IsMobile(`
        max-width: 480px;
        margin: auto;
      `)}
    `}
  }
  .detail-content_list {
    display: flex;
    flex-wrap: wrap;
    margin-top: ${SpacingSize.XL}px;
    li {
      width: calc(33.33% - 21.33px);
      background: ${Color.White};
      border-radius: ${RadiusSize.Medium}px;
      ${Shadow.Deep};
      padding: ${SpacingSize["2XL"]}px ${SpacingSize.XL}px ${SpacingSize.XL}px ${SpacingSize.XL}px;
      text-align: center;
      &:nth-child(n + 4) {
        margin-top: ${SpacingSize["2XL"]}px;
      }
      &:not(:nth-child(3n + 1)) {
        margin-left: ${SpacingSize["2XL"]}px;
      }
      ${css`
        ${IsMobile(`
          width: calc(50% - 12px);
          padding: ${SpacingSize["XL"]}px ${SpacingSize.M}px ${SpacingSize.M}px
        ${SpacingSize.M}px;
          &:nth-child(n + 4) {
            margin-top: 0;
          }
          &:not(:nth-child(3n + 1)) {
            margin-left: 0;
          }
          &:nth-child(n + 3) {
            margin-top: ${SpacingSize["XL"]}px;
          }
          &:nth-child(2n) {
            margin-left: ${SpacingSize["XL"]}px;
          }
        `)}
      `}
    }
  }
  .detail-content_list-heading {
    margin-top: ${SpacingSize.S}px;
    ${css`
      ${IsMobile(`
        margin-top: ${SpacingSize.XS}px;
      `)}
    `}
  }
  .detail-content_list-supplement {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    margin-top: ${SpacingSize["3XS"]}px;
    ${css`
      ${IsMobile(`
        height: 39px;
      `)}
    `}
  }
`

interface ModalProps {
  open: boolean
  close: Function
  popupAreaClassName?: string
  graduatesIn?: number
}
interface StyledModalProps {
  open?: boolean
}

const ApplyDoneModal = ({ open, close, popupAreaClassName, graduatesIn }: ModalProps) => {
  return (
    <>
      <StyledDialogWrapper open={open}>
        <div className={popupAreaClassName}>
          <StyledDialogInner>
            <StyledDialogHeading>
              {graduatesIn === 2023 ? (
                <>23卒面談の先行受付はまだ完了していません（夏頃開催予定）</>
              ) : (
                <>お申し込みはまだ完了していません</>
              )}
            </StyledDialogHeading>
            <StyledDialogContent>
              <StyledDescription>
                {graduatesIn === 2023 ? (
                  <>
                    <span className="attention">公式LINE</span>にご登録いただき、トークルームから
                    <span className="attention">氏名</span>
                    をお送りください。開催次第、優先して面談の日程調整や詳細をLINEにてご案内致します。
                  </>
                ) : (
                  <>
                    続けて<span className="attention">公式LINE</span>にご登録いただき、トークルームから
                    <span className="attention">氏名</span>
                    をお送りください。その後、面談の日程調整や詳細をLINEにてご案内致します。
                  </>
                )}
              </StyledDescription>
              <Pc>
                <StyledQrWrapper>
                  <img
                    src={`${staticPath(
                      `/static/images/new-grad-job/consultation/${
                        graduatesIn === 2023 ? "line-qr_23-graduates.png" : "line-qr_22-graduates.png"
                      }`
                    )}`}
                    className="qr-img"
                  />
                </StyledQrWrapper>
              </Pc>
            </StyledDialogContent>
            <StyledActionPairButtonWrapper>
              <Sp>
                <SnsButton
                  size="Medium"
                  snsType="Line"
                  onClick={() =>
                    window.open(`${graduatesIn === 2023 ? "https://lin.ee/v9j1WXe" : "https://lin.ee/hrO6saG"}`)
                  }
                >
                  友達に追加する
                </SnsButton>
              </Sp>
              <FunctionLink size="Medium" onClick={close} className="close">
                閉じる
              </FunctionLink>
            </StyledActionPairButtonWrapper>
          </StyledDialogInner>
        </div>
      </StyledDialogWrapper>
      <StyledDialogOverlay open={open} />
    </>
  )
}

const StyledDialogWrapper = styled.div`
  width: calc(100% - 32px);
  max-width: 400px;
  margin: auto;
  display: none;
  position: fixed;
  top: 20%;
  left: 0;
  right: 0;
  z-index: 100;
  ${({ open }: StyledModalProps) => css`
    ${open &&
    css`
      display: block;
    `}
  `}
`
const StyledDialogInner = styled.div`
  padding: ${SpacingSize.XL}px;
  background-color: ${Color.White};
  border-radius: ${RadiusSize.Medium}px;
  ${Shadow.Deep};
`

const StyledDialogOverlay = styled.div`
  position: fixed;
  z-index: 99;
  top: -25%;
  left: 0;
  bottom: 0;
  right: 0;
  height: 125%;
  width: 100%;
  background: #000;
  display: none;
  opacity: 0.6;
  ${({ open }: StyledModalProps) => css`
    ${open &&
    css`
      display: block;
    `}
  `}
`

const StyledDialogHeading = styled.div`
  margin-bottom: ${SpacingSize["3XS"]}px;
  font-size: ${TextSize.L}px;
  font-weight: bold;
  line-height: ${LineHeight.S}%;
`

const StyledDialogContent = styled.div`
  margin-bottom: ${SpacingSize.M}px;
  font-size: ${TextSize.S}px;
  line-height: ${LineHeight.M}%;
`

const StyledDescription = styled.div`
  margin-bottom: ${SpacingSize.M}px;
  font-size: ${TextSize.S}px;
  line-height: ${LineHeight.M}%;
  color: ${Color.Gray};
  .attention {
    color: ${Color.Attention};
    font-weight: bold;
  }
  .sp-hide {
    ${IsMobile(css`
      display: none;
    `)}
  }
`

const StyledQrWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  .qr-img {
    width: 180px;
  }
`

const StyledActionPairButtonWrapper = styled.div`
  max-width: 320px;
  margin: auto;
  ${IsMobile(css`
    max-width: 280px;
  `)}
  .close {
    text-align: center;
    padding: ${SpacingSize.M}px 0;
  }
`

export default NewGradJobConsultation
